import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CrawfordDialogComponent, CrawfordSnackbarService, DialogButton } from 'crawford-shell';
import { global } from '../../app.settings';
import { AdvancedSearch } from '../../claim/model/advanced-search.model';
import { UserDetails } from '../../user/user-details.model';
import { UserService } from '../../user/user.service';
import { SavedSearch } from '../model/saved-search.model';
import { SavedSearchService } from '../saved-search.service';
import { NamedObject } from '../model/named-object.model';


@Component({
  selector: 'app-save-search',
  templateUrl: './save-search-dialog.component.html',
  styleUrls: ['./save-search-dialog.component.scss']
})
export class SaveSearchDialogComponent implements OnInit {
  public savedSearchForm: FormGroup;
  public actionText = 'Save';
  public nameAlreadyExists: boolean;

  private userDetails: UserDetails;
  private idToOverwrite: number;
  private retrievedSearch: SavedSearch;
  private rawSearchValue: any;
  private existingSavedSearchNames: NamedObject[];

  @ViewChild('saveSearchDialog') private savedSearchDialog: CrawfordDialogComponent;

  dialogButtons = [
    new DialogButton(this.saveSearch.bind(this), { content: 'Save', isPrimary: true }),
    new DialogButton(this.cancelSavedSearch.bind(this), { content: 'Cancel', isPrimary: false })
  ];

  constructor(
    private formBuilder: FormBuilder,
    private savedSearchService: SavedSearchService,
    private snackbarService: CrawfordSnackbarService,
    private userService: UserService
  ) { }

  public ngOnInit(): void {
    this.createSaveSearchForm();
    this.userDetails = this.userService.userDetails;

    this.loadExistingSearchNames(searches => this.existingSavedSearchNames = searches);
  }

  private loadExistingSearchNames(successfulCallback: any): void {
    this.savedSearchService.getExistingSearchNames().subscribe(
      searches => {
        successfulCallback(searches);
      });
  }

  public openDialog(searchData: AdvancedSearch): void {
    this.rawSearchValue = searchData;
    this.retrievedSearch = this.savedSearchService.getRetrievedSearch();

    if (this.retrievedSearch) {
      this.savedSearchForm.controls.name.setValue(this.retrievedSearch.searchName);
    } else {
      this.savedSearchForm.controls.name.setValue('');
      this.nameAlreadyExists = false;
      this.actionText = 'Save';
    }

    this.savedSearchDialog.showDialog();
  }

  private createSaveSearchForm(): void {
    this.savedSearchForm = this.formBuilder.group({
      name: new FormControl('', [ Validators.required ])
    });

    this.savedSearchForm.controls.name.valueChanges.subscribe(
      value => this.nameChanged(value)
    );
  }

  private nameChanged(value: string): void {
    if (!value || !this.existingSavedSearchNames) {
      return;
    }

    let isOverwrite = false;
    this.nameAlreadyExists = false;
    this.idToOverwrite = null;

    this.existingSavedSearchNames.forEach(x => {
      if (x.name.toLowerCase() !== value.toLowerCase()) {
        return;
      }

      isOverwrite = true;

      if (!this.retrievedSearch || x.id !== this.retrievedSearch.id) {
        this.nameAlreadyExists = true;
        this.idToOverwrite = x.id;
      } else if (x.id === this.retrievedSearch.id) {
        this.idToOverwrite = x.id;
      }
    });

    this.actionText = isOverwrite ? 'Overwrite' : 'Save';
  }

  public saveSearch(): void {
    if (this.savedSearchForm.invalid) {
      return;
    }

    const searchToSave = this.buildSearchToSave();

    if (searchToSave.id) {
      this.updateSavedSearch(searchToSave);
    } else {
      this.createSavedSearch(searchToSave);
    }
  }

  private createSavedSearch(searchToSave: SavedSearch): void {
    this.savedSearchService.createSavedSearch(searchToSave).subscribe(
      result => {
        this.successfulSaveCallback(searchToSave);
      });
  }

  private updateSavedSearch(searchToSave: SavedSearch): void {
    this.savedSearchService.updateSavedSearch(searchToSave, searchToSave.id).subscribe(
      result => {
        this.successfulSaveCallback(searchToSave);
      });
  }

  private successfulSaveCallback(savedSearch: SavedSearch) {
    this.savedSearchDialog.hideDialog();
    this.snackbarService.displaySuccessMessage('Search Saved');
    this.savedSearchService.setRetrievedSearch(savedSearch);

    this.loadExistingSearchNames(searches => {
      this.existingSavedSearchNames = searches;
    });
  }

  private buildSearchToSave(): SavedSearch {
    const name = this.savedSearchForm.value.name;

    if (!name) {
      throw new Error('The search to be saved needs a name');
    }

    const searchToSave = new SavedSearch();

    if (this.idToOverwrite) {
      searchToSave.search = JSON.stringify(this.rawSearchValue);
      searchToSave.id = this.idToOverwrite;
      searchToSave.searchName = this.savedSearchForm.value.name;
    } else {
      searchToSave.search = JSON.stringify(this.rawSearchValue);
      searchToSave.searchName = name;
    }

    return searchToSave;
  }

  public cancelSavedSearch(): void {
    this.savedSearchDialog.hideDialog();
  }
}



// WEBPACK FOOTER //
// D:/a/1/s/UI/src/app/saved-search/save-search-dialog/save-search-dialog.component.ts